import { useEffect, useRef, useState, useCallback } from "react";
import type { fluidPay } from "@fluid-app/fluid-pay-core";

export type VgsFieldState = {
  isFocused: boolean;
  isValid: boolean;
  isEmpty: boolean;
  cardType?: string;
  bin?: string;
  last4?: string;
};

export type VgsFormState = {
  [key: string]: VgsFieldState;
};

interface VGSCollectForm {
  setRouteId: (routeId: string) => void;
  field: (selector: string, config: Record<string, unknown>) => void;
  submit: (
    path: string,
    options: { headers?: Record<string, string> },
    successCallback: (
      status: number,
      response: fluidPay.VgsCollectSubmitResponse,
    ) => void,
    errorCallback: (errors: Record<string, string[]>) => void,
  ) => void;
  unmount: () => void;
}

interface VGSCollectLibrary {
  create: (
    vaultId: string,
    environment: string,
    stateCallback: (state: Record<string, Partial<VgsFieldState>>) => void,
  ) => VGSCollectForm;
}

const getVGSCollect = (): VGSCollectLibrary | null => {
  if (typeof window !== "undefined") {
    return (
      (window as unknown as { VGSCollect?: VGSCollectLibrary }).VGSCollect ||
      null
    );
  }
  return null;
};

const DEFAULT_FIELD_STATE: VgsFormState = {
  card_number: { isFocused: false, isValid: false, isEmpty: true },
  card_exp: { isFocused: false, isValid: false, isEmpty: true },
  card_cvc: { isFocused: false, isValid: false, isEmpty: true },
  card_holder: { isFocused: false, isValid: false, isEmpty: true },
};

export function useVgsCollect() {
  const [isFormReady, setIsFormReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [scriptLoadError, setScriptLoadError] = useState<string | undefined>(
    undefined,
  );

  const [vgsFieldState, setVgsFieldState] =
    useState<VgsFormState>(DEFAULT_FIELD_STATE);

  const formRef = useRef<VGSCollectForm | null>(null);
  const onSuccessRef = useRef<
    ((response: fluidPay.VgsCollectSubmitResponse) => void) | null
  >(null);
  const onErrorRef = useRef<((error: string) => void) | null>(null);

  // Load VGS Collect script
  useEffect(() => {
    if (typeof window === "undefined") return;

    const vgsCollect = getVGSCollect();
    if (vgsCollect) {
      const timeoutId = window.setTimeout(() => {
        setIsScriptLoaded(true);
      }, 0);
      return () => window.clearTimeout(timeoutId);
    }

    if (isScriptLoaded) return;

    const script = document.createElement("script");
    script.src =
      "https://js.verygoodvault.com/vgs-collect/3.3.0/vgs-collect.js";
    script.async = true;
    script.onload = () => setIsScriptLoaded(true);
    // Surface load failures both to a registered consumer (if any) and to
    // state, so a consumer mounting after the failure still sees it.
    script.onerror = () => {
      const message = "Failed to load payment form";
      setScriptLoadError(message);
      onErrorRef.current?.(message);
    };
    document.head.appendChild(script);
  }, [isScriptLoaded]);

  const updateFieldState = useCallback(
    (state: Record<string, Partial<VgsFieldState>>) => {
      if (!state) return;

      setVgsFieldState((prev) => {
        const newState: VgsFormState = { ...prev };
        let hasChanges = false;

        Object.keys(state).forEach((key) => {
          const field = state[key];
          if (!field) return;

          const next: VgsFieldState = {
            isFocused: !!field.isFocused,
            isValid: !!field.isValid,
            isEmpty: !!field.isEmpty,
            cardType: field.cardType,
            bin: field.bin,
            last4: field.last4,
          };

          if (
            !newState[key] ||
            JSON.stringify(newState[key]) !== JSON.stringify(next)
          ) {
            newState[key] = next;
            hasChanges = true;
          }
        });

        return hasChanges ? newState : prev;
      });
    },
    [],
  );

  const resetVgs = useCallback(() => {
    if (formRef.current) {
      try {
        formRef.current.unmount();
      } catch (e) {
        console.warn("VGS unmount error", e);
      }
      formRef.current = null;
    }

    setIsFormReady(false);
    setVgsFieldState({ ...DEFAULT_FIELD_STATE });
  }, []);

  const initializeForm = useCallback(
    async (credentials: fluidPay.VaultResponse) => {
      if (!isScriptLoaded) return;

      const VGSCollect = getVGSCollect();
      if (!VGSCollect) {
        onErrorRef.current?.("Payment script not loaded");
        return;
      }

      try {
        setIsLoading(true);

        const vgsEnvironment =
          credentials.vault.environment === "sandbox" ? "sandbox" : "live-eu-1";

        const form = VGSCollect.create(
          credentials.vault.vault_id,
          vgsEnvironment,
          (state) => updateFieldState(state),
        );

        if (credentials.vault.route_id) {
          form.setRouteId(credentials.vault.route_id);
        }

        formRef.current = form;

        // Wait for DOM elements
        let retries = 0;
        const maxRetries = 20;
        const retryDelay = 200;

        const waitForDom = async (): Promise<boolean> => {
          const allExist = [
            "vgs-card-number",
            "vgs-expiration-date",
            "vgs-cvc",
            "vgs-card-holder",
          ].every((id) => {
            const el = document.getElementById(id);
            return el && el.isConnected;
          });

          if (allExist) return true;
          if (retries >= maxRetries) return false;

          retries++;
          await new Promise((resolve) => setTimeout(resolve, retryDelay));
          return waitForDom();
        };

        const domReady = await waitForDom();
        if (!domReady) {
          throw new Error("Payment form elements not ready");
        }

        // Resolve theme tokens from the wrapper element. The theme engine
        // applies `data-theme` on a subtree element (not <html>), so CSS
        // variables aren't visible on document.documentElement — only on a
        // node inside the themed subtree. Cross-origin iframe contents
        // can't see parent CSS variables, so we resolve concrete values
        // here and pass them to VGS.
        //
        // The `vgs-card-number` element is guaranteed to exist at this
        // point: the `waitForDom` check above blocks until all four VGS
        // wrapper elements are mounted. The hardcoded fallback colors only
        // activate if the CSS variables themselves are unset.
        const themedNode = document.getElementById("vgs-card-number");
        const probeStyle = themedNode ? getComputedStyle(themedNode) : null;
        const foregroundColor =
          probeStyle?.getPropertyValue("--color-foreground").trim() ||
          "#0a0a0a";
        const mutedForegroundColor =
          probeStyle?.getPropertyValue("--color-muted-foreground").trim() ||
          "#737373";

        // Wrapper is `h-9 py-1` (Input primitive). Content area = 36 - 2 (border) - 8 (py-1) = 26px.
        const css = {
          fontSize: "14px",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          color: foregroundColor,
          background: "transparent",
          paddingTop: "0px",
          paddingBottom: "0px",
          paddingLeft: "0px",
          paddingRight: "0px",
          boxSizing: "border-box",
          lineHeight: "26px",
          height: "100%",
          width: "100%",
          "&::placeholder": { color: mutedForegroundColor, opacity: 1 },
          "&:focus": { outline: "none" },
        };

        form.field("#vgs-card-number", {
          type: "card-number",
          name: "card_number",
          placeholder: "0000 0000 0000 0000",
          showCardIcon: true,
          css: { ...css, paddingRight: "40px" },
          autoComplete: "cc-number",
          validations: ["required", "validCardNumber"],
        });

        form.field("#vgs-expiration-date", {
          type: "card-expiration-date",
          name: "card_exp",
          yearLength: 2,
          placeholder: "MM / YY",
          css,
          autoComplete: "cc-exp",
          validations: ["required", "validCardExpirationDate"],
        });

        form.field("#vgs-cvc", {
          type: "card-security-code",
          name: "card_cvc",
          placeholder: "123",
          css,
          autoComplete: "cc-csc",
          validations: ["required", "validCardSecurityCode"],
        });

        form.field("#vgs-card-holder", {
          type: "text",
          name: "card_holder",
          placeholder: "Full Name",
          css,
          autoComplete: "cc-name",
          validations: ["required"],
        });

        setIsFormReady(true);
      } catch (error) {
        console.error("Failed to initialize VGS:", error);
        onErrorRef.current?.("Failed to initialize payment form");
      } finally {
        setIsLoading(false);
      }
    },
    [isScriptLoaded, updateFieldState],
  );

  const submitCard = useCallback(() => {
    if (!formRef.current) {
      onErrorRef.current?.("Form not initialized");
      return;
    }

    formRef.current.submit(
      `/post`,
      {},
      (status: number, response: fluidPay.VgsCollectSubmitResponse) => {
        if (status >= 200 && status < 400) {
          onSuccessRef.current?.(response);
        } else {
          onErrorRef.current?.("Payment failed");
        }
      },
      (errors: Record<string, string[]>) => {
        const allErrors = Object.values(errors)
          .flat()
          .filter(
            (msg) =>
              typeof msg === "string" &&
              msg.trim().length > 0 &&
              msg !== "Required",
          );

        const uniqueErrors = Array.from(new Set(allErrors));

        let errorMsg = "Please verify your payment details.";
        if (uniqueErrors.length > 0) {
          errorMsg = uniqueErrors.join(", ");
        } else {
          const rawErrors = Object.values(errors).flat();
          if (rawErrors.length > 0) {
            errorMsg = "Please fill in all required fields.";
          }
        }

        onErrorRef.current?.(errorMsg);
      },
    );
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (formRef.current) {
        try {
          formRef.current.unmount();
        } catch (e) {
          console.warn("VGS unmount error", e);
        }
        formRef.current = null;
      }
    };
  }, []);

  return {
    isFormReady,
    isLoading,
    isScriptLoaded,
    scriptLoadError,
    initializeForm,
    submitCard,
    vgsFieldState,
    resetVgs,
    onSuccessRef,
    onErrorRef,
  };
}
